import React from 'react';
import PropTypes from 'prop-types';
import { Dropdown, MenuItem } from 'react-bootstrap';
import { connect } from 'react-redux';
import each from 'lodash/each';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import {
  BrowserRouter as Router,
  Switch,
  Route
} from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  faBullhorn,
  faClipboard,
  faCogs,
  faDollarSign,
  faFileAlt,
  faHandshake,
  faHome,
  faLocationArrow,
  faBolt,
  faPaperclip,
  faFileInvoiceDollar,
  faCheck,
  faTh
} from '@fortawesome/pro-solid-svg-icons';

import confirm from 'components/confirm';
import AssignmentPropTypes from '../assignment_prop_types';
import TaskList from 'components/project/tasks/task_list';
import ActivityFeed from '../../project/components/activity_feed';
import AssignmentOverview from './assignment_overview';
import DetailsCard from './details_card';
import SurveyTab from './survey_tab';
import DocsTab from 'components/project/docs_tab';
import {
  fetchTasks,
  showNewNoteModal,
  showNewTaskModal
} from '../../project/actions/index';
import { fetchFolder } from 'components/documents/actions';
import { deleteProject, updateCollaborators, updateNotificationsBlockedUserIds } from 'components/project/actions';
import { toggleReject, removeSurveyProperty } from 'components/assignments/actions';
import ProjectLayout from 'components/project_layout/project_layout';
import LayoutHeader from 'components/project_layout/layout_header';
import TaskModal from 'components/project/task_modal';
import UnstyledDropdownToggle from 'components/bootstrap_extensions/unstyled_dropdown_toggle';
import DuplicationForm from 'components/project/duplication_form';
import PropertyTab from '../../project/components/property_tabs/property_tab';
import VouchersTab from 'bundles/project/components/vouchers_tab';

const ASSIGNMENT_TAB = {
  icon: faBullhorn,
  subtabs: {
    details: {
      icon: faClipboard
    },
    survey: {
      icon: faLocationArrow
    },
    docs: {
      label: 'Documents',
      icon: faFileAlt
    }
  }
};

export class AssignmentNavigator extends React.Component {
  static propTypes = {
    activeTab: PropTypes.string,
    ...AssignmentPropTypes,
    canDuplicateAssignment: PropTypes.bool.isRequired,
    editPath: PropTypes.string,
    features: PropTypes.shape({
      projectAttachments: PropTypes.bool,
      projectTasksActivities: PropTypes.bool
    }).isRequired,
    fetchFolder: PropTypes.func.isRequired,
    fetchTasks: PropTypes.func.isRequired,
    helpCenterUrl: PropTypes.string,
    showNewNoteModal: PropTypes.func,
    showNewTaskModal: PropTypes.func,
    surveyPropertyCsvColumns: PropTypes.object,
    surveysPath: PropTypes.string,
    tasksCsvPath: PropTypes.string,
    updateNotificationsBlockedUserIds: PropTypes.func.isRequired,
    vouchersSearchPath: PropTypes.string
  }

  state = {
    showDuplicationForm: false
  }

  componentDidMount() {
    const { fetchTasks, features } = this.props;

    if (features.projectTasksActivities) {
      fetchTasks();
    }
  }

  DEAL_TABS = {
    'vouchers': {
      icon: faFileInvoiceDollar,
      path: this.props.vouchersSearchPath
    },
    'royalties': {
      icon: faDollarSign,
      label: 'Royalties',
      path: this.props.royaltiesTabPath
    }
  };

  fetchDocuments = (marketing) => {
    const { fetchFolder, project } = this.props;
    const folderId = marketing ? project.rootMarketingDocumentFolderId : project.rootNonMarketingDocumentFolderId;
    fetchFolder(project.id, folderId);
  }

  projectIsPersisted = () => !!this.props.project.id;

  canEditProject = () => this.projectIsPersisted() && !!this.props.project.editLink;

  updateCollaborators = (selectedUsers) => {
    return this.props.updateCollaborators(this.props.project.id, selectedUsers);
  };

  editAssignment = () => {
    if (this.projectIsPersisted()) {
      window.location.assign(this.props.editPath);
    }
  }

  deleteAssignment = () => {
    const message = 'Deleting an Assignment is permanent. ' +
      'You won\'t be able to access any of its information or photos.';
    confirm(message, {
      buttonLabel: 'Delete Assignment',
      buttonStyle: 'danger',
      title: `Delete ${this.props.assignment.requirement.assignmentTitle}?`
    }).then(() => {
      this.props.deleteProject(this.props.project.id, (redirectPath) => {
        window.location.assign(redirectPath);
      });
    });
  }

  showDuplicationForm = () => {
    this.setState({ showDuplicationForm: true });
  }

  hideDuplicationForm = () => {
    this.setState({ showDuplicationForm: false });
  }

  showDocsTab = () => this.props.project.canViewUnreleasedFeatures

  showDealsTab() {
    return find(this.DEAL_TABS, tab => tab.path);
  }

  showTaskActivityTabs() {
    return this.props.features.projectTasksActivities && this.projectIsPersisted();
  }

  showAttachmentsTab() {
    return this.props.features.projectAttachments && this.projectIsPersisted();
  }

  showSurveyTab() {
    return this.props.features.crm && this.projectIsPersisted();
  }

  availableTabs() {
    const tabs = {
      project: {
        icon: faTh,
        subtabs: {}
      }
    };

    if (isEmpty(this.props.features)) {
      tabs.project.subtabs.overview = {
        icon: faHome
      };
      return tabs;
    }

    if (this.showTaskActivityTabs()) {
      tabs.project.subtabs.overview = {
        icon: faHome
      };
      tabs.project.subtabs.tasks = {
        icon: faCheck
      };
      tabs.project.subtabs.activities = {
        label: 'Activity Feed',
        icon: faBolt
      };
    }

    if (this.showAttachmentsTab()) {
      tabs.project.subtabs['attachments'] = {
        icon: faPaperclip
      };
    }

    tabs.assignment = ASSIGNMENT_TAB;

    if (this.showDealsTab()) {
      tabs.deal = {
        icon: faHandshake,
        subtabs: {}
      };

      each(this.DEAL_TABS, (tabData, key) => {
        if (tabData.path) tabs.deal.subtabs[key] = tabData;
      });

      if (!this.props.project.canEditVouchers) delete tabs.deal.subtabs.vouchers;
    }

    if (!this.showSurveyTab()) delete tabs.assignment.subtabs.survey;
    if (!this.showDocsTab()) delete tabs.assignment.subtabs.docs;

    return tabs;
  }

  renderCTAButtons() {
    if (!this.props.editPath) return;

    return (
      <div className="text-right mb-2">
        <button className="btn btn-dark btn-sm" id="edit-assignment-btn" onClick={this.editAssignment}>
          Edit Assignment
        </button>
        <Dropdown disabled={!this.projectIsPersisted()} id="project-action-dropdown">
          <UnstyledDropdownToggle
            bsRole="toggle"
            className="right-buffer-md"
            id="project-action-button"
            variant="dark"
          >
            <button className="btn btn-dark btn-sm left-buffer-md" disabled={!this.projectIsPersisted()}>
              <FontAwesomeIcon icon={faCogs} size="lg"/>
            </button>
          </UnstyledDropdownToggle>
          <Dropdown.Menu className="dropdown-menu-right">
            <MenuItem eventKey={1} id="delete-assignment-btn" onClick={this.deleteAssignment}>
              <strong>Delete Assignment</strong>
            </MenuItem>
            {this.props.canDuplicateAssignment &&
              <MenuItem eventKey={2} id="duplicate-assignment-btn" onClick={this.showDuplicationForm}>
                <strong>Duplicate Assignment</strong>
              </MenuItem>}
          </Dropdown.Menu>
        </Dropdown>
      </div>
    );
  }

  renderTabContent() {
    return (
      <div className="container-fluid py-4">
        <Switch>
          <Route path="/overview">
            <AssignmentOverview
              editPath={this.props.editPath}
              helpCenterUrl={this.props.helpCenterUrl}
              showNewNoteModal={this.props.showNewNoteModal}
              showNewTaskModal={this.props.showNewTaskModal}
            />
          </Route>
          {this.renderWorkspaceTabs()}
        </Switch>
      </div>
    );
  }

  renderWorkspaceTabs() {
    if (isEmpty(this.props.features)) return;

    const workspaceRoutes = [];

    if (this.showTaskActivityTabs()) {
      workspaceRoutes.push(
        <Route key="tasks" path="/tasks">
          <TaskList
            defaultTaskAssignee={this.props.defaultTaskAssignee}
            name="task-list"
            tasksCsvPath={this.props.tasksCsvPath}
          />
        </Route>
      );

      workspaceRoutes.push(
        <Route key="activities" path="/activities">
          <ActivityFeed/>
        </Route>
      );
    }

    workspaceRoutes.push(
      <Route key="attachments" path="/attachments">
        <DocsTab/>
      </Route>
    );

    workspaceRoutes.push(
      <Route key="details" path="/details">
        <DetailsCard />
      </Route>
    );

    if (this.showSurveyTab()) {
      workspaceRoutes.push(
        <Route key="survey" path="/survey">
          <SurveyTab
            addBuySurveyPropertyPath={this.props.assignment.addBuySurveyPropertyPath}
            addLeaseSurveyPropertyPath={this.props.assignment.addLeaseSurveyPropertyPath}
            projectId={this.props.project.id}
            removeSurveyProperty={this.props.removeSurveyProperty}
            surveyProperties={this.props.assignment.tenantRepProperties}
            surveyPropertyCsvColumns={this.props.surveyPropertyCsvColumns}
            surveysPath={this.props.surveysPath}
            toggleReject={this.props.toggleReject}
          />
        </Route>
      );
    }

    if (this.showDocsTab()) {
      workspaceRoutes.push(
        <Route key="docs" path="/docs">
          <DocsTab
            docTemplateOptions={this.props.docTemplateOptions}
            marketing
          />
        </Route>
      );
    }

    if (this.props.vouchersSearchPath) {
      workspaceRoutes.push(
        <Route key="vouchers" path="/vouchers">
          <VouchersTab
            proposal={this.props.project.listing.listingStatus === 'proposal'}
            showForAssignment
            vouchersSearchPath={this.props.vouchersSearchPath}
          />
        </Route>
      );
    }

    if (this.props.royaltiesTabPath) {
      workspaceRoutes.push(
        <Route key="royalties" path="/royalties">
          <PropertyTab tabPath={this.props.royaltiesTabPath} />
        </Route>
      );
    }

    return workspaceRoutes;
  }

  render() {
    return (
      <Router basename={`/assignments/${this.props.project.id}`}>
        <div className="project-wrapper">
          <LayoutHeader
            collaborators={this.props.project.collaborators}
            status={this.props.assignment.statusLabel}
            thumbnailUrl={this.props.project.thumbnailUrl}
            title={this.props.assignment.requirement.assignmentTitle}
            onCollaboratorsUpdated={this.canEditProject() ? this.updateCollaborators : null}
            onUpdateNotificationsBlockedUserIds={this.props.updateNotificationsBlockedUserIds}
          >
            {this.renderCTAButtons()}
          </LayoutHeader>
          <ProjectLayout activeTab={this.props.activeTab} tabs={this.availableTabs()}>
            {this.renderTabContent()}
          </ProjectLayout>
        </div>
        {this.props.features.projectTasksActivities && (
          <React.Fragment>
            <TaskModal defaultTaskAssignee={this.props.defaultTaskAssignee} />
            {this.props.project.id && (
              <DuplicationForm
                assignment
                projectId={this.props.project.id}
                projectTitle={this.props.assignment.requirement.assignmentTitle}
                show={this.state.showDuplicationForm}
                onHide={this.hideDuplicationForm}
              />
            )}
          </React.Fragment>
        )}
      </Router>
    );
  }
}

const mapStateToProps = ({ assignment, project, ui, features }) => (
  { activeTab: ui.activeTab, assignment, project, features }
);

export default connect(
  mapStateToProps,
  {
    deleteProject,
    fetchFolder,
    fetchTasks,
    removeSurveyProperty,
    showNewNoteModal,
    showNewTaskModal,
    toggleReject,
    updateCollaborators,
    updateNotificationsBlockedUserIds
  }
)(AssignmentNavigator);
